<script lang="ts">
	import type { Blog, Bootcamp } from "$lib/datamodel";
	import type { Infer, SuperValidated } from "sveltekit-superforms";
	import dayjs from 'dayjs';
	import advancedFormat from 'dayjs/plugin/advancedFormat';
	import CaptureContactBlog from "./CaptureContactBlog.svelte";
	import type { ContactCaptureSchema } from "./forms/CaptureContact.svelte";
	import BlogAuthor from "./BlogAuthor.svelte";
	import { isRoles } from "$lib/client/services/user";
	import Button from "./ui/button/button.svelte";
	import { user } from "$lib/client/stores/nucamp-user";
	export let blog: Blog;
	export let bootcamps: Bootcamp[];
	export let supForm: SuperValidated<Infer<ContactCaptureSchema>>;
	dayjs.extend(advancedFormat)

	let isBlogEditor: boolean = false;
	$: isBlogEditor = isRoles($user, ['admin','blogeditor']);

	let bootcamp: Bootcamp | undefined;
	$: bootcamp = blog.paywallasset?.bootcampId ? bootcamps.find((b) => b.id === blog.paywallasset?.bootcampId) : undefined;
</script>

<article>
	{#if blog}
		<div class="px-3 md:px-0">
			<h1 class="font-bold text-4xl antialiased tracking-tight">{blog.title}</h1>
				<div class="text-xs flex items-center pt-4 pb-2 md:pt-1">
						{#if isBlogEditor}
							<div class="pr-2">
									<Button href="/blogedit/{blog.shortid}/edit" variant="secondary" size="sm">Edit</Button>
							</div>
						{/if}
						<div class="text-xs flex flex-grow justify-between pt-4 pb-2 md:pt-1">
							<p>By <a href="/blogs/authors/{blog.authorDetails.authorUrl}?page=1" class="text-nucamp-light font-semibold hover:text-nucamp-light-hover hover:underline underline-offset-2 decoration-dotted">{blog.author}</a></p>
							<p>Last Updated: <strong>{dayjs(blog.updatedAt).format("MMMM Do YYYY")}</strong></p>
						</div>
				</div>
		</div>
		<div class="flex justify-center">
			{#if blog.media?.list && blog.media?.list[0]}
				<img fetchpriority="high" loading="eager" class="blogthumbnail" src={blog.media?.list[0].url} alt={blog.metaimagealt} />
			{/if}
		</div>
			{#each blog.contentChunks as chunk, i }
				<div class="blogcontent">{@html chunk}</div>
				{#if i <= 3 && ((i >= 1 && blog.contentChunks[i].length >= 1500) || i === 0) && !(i === blog.contentChunks.length - 1)}
					<div class="px-0 pt-4">
						{#await supForm}
							Loading form...
						{:then form}
							<CaptureContactBlog supForm={form} {bootcamps} {blog} {bootcamp}/>
						{:catch error}
							<p>{error.message}</p>
						{/await}
					</div>
				{/if}
			{/each}
		<BlogAuthor blogAuthor={blog.authorDetails} mode={"blogpage"}/>
	{/if}
</article>

<style global lang="postcss">
	.blogthumbnail {
		box-shadow: 0 0 20px rgb(0 0 0 / 15%);
		@media screen and (max-width: 299px) {
						height: 150px;
						object-fit: fill;
						width: 100%;
		}
		@media screen and (min-width: 300px) and (max-width: 499px) {
						height: auto;
						object-fit: fill;
						width: 100%;
		}
		@media screen and (min-width: 500px) and (max-width: 992px) {
						height: 300px;
						object-fit: fill;
						width: auto;
		}
		@media screen and (min-width: 992px) and (max-width: 1018px) {
						height: 400px;
						object-fit: fill;
						width: 100%;
		}
		@media screen and (min-width: 1018px) and (max-width: 1200px) {
						height: 325px;
						object-fit: fill;
						width: 100%;
		}
		@media screen and (min-width: 1200px) {
						height: 400px;
						object-fit: fill;
						width: 100%;
		}
		margin: 8px 0 2rem 0;
		@apply rounded-none md:rounded-lg;
	}

	.blogcontent {
		@apply px-3 md:px-0;
		@media screen and (max-width: 1000px) {
				img {
						height: 100%;
						object-fit: cover;
						width: 100%;
				}
		}
		@media screen and (min-width: 1001px) {
				img {
						height: 100%;
						max-width: 700px;
						object-fit: cover;
						width: 100%;
				}
		}
		em {
				@apply italic;
		}
		a {
				@apply font-medium underline underline-offset-4 decoration-dashed text-nucamp-light hover:text-nucamp-light-hover;
		}
		blockquote {
				font-size: x-large;
				margin: 16px 0;
				line-height: 1.7em;
				border-left: 6px solid #a83ea2;
				padding-left: 31px;
		}
		p{
				@apply text-lg font-normal leading-8 antialiased my-4;
		}
		#nextyeararticle {
			font-size: smaller;
			font-style: italic;
			margin-top: 0px;
			margin-bottom: 2rem;
		}
		#table-of-contents-h3 {
								@apply text-2xl font-bold mb-4;
		}
		#table-of-contents-ul {
				padding-bottom: 30px;
				LI {
						cursor: pointer;
						.TOCTitle {
								margin: 8px 0;
								@apply text-lg text-nucamp-light hover:text-nucamp-light-hover border-b border-dashed border-nucamp-light hover:border-nucamp-primary-hover;
						}
						width: fit-content;     
						line-height: 1.4;
				}
		}
		H1{
				text-align: left;
				font-weight: 800;
				font-size: 2.6rem;
		}
		H2{
				text-align: left;
				font-weight: 800;
				padding: 40px 0 8px 0;
				font-size: 2.6rem;
		}
		H3{
				text-align: left;
				margin: 16px 0 16px 0;
				font-weight: 700;
				font-size: 2.2rem;
		}
		.h2Div {
				padding: 40px 0 8px;
				H2 {
						text-align: left;
						padding: 0px;
												@apply text-3xl font-bold;
						display: inline;
				}
		}
		H5{
				text-align: left;
				font-weight: 400;
				font-size: 1.5rem;
				font-family: 'Inter';
				margin: 1rem 0 2rem 0;
		}
		UL, OL {
				@apply px-8 md:px-12;
				@apply list-disc;
		}
		LI{
				font-size: 1.5rem;
				line-height: 1.8;
				@apply text-lg;
				margin: 8px 0;
		}
		code {
				background-color: #f3f3f3;
				border-radius: 5px;
				padding: 2px 4px;
				color: #444449;
				font-family: 'Fira Code', monospace;
		}
		table {
				width: 100%; 
				border-collapse: collapse;
				text-align: left;
				font-family: Arial, sans-serif;
				padding: 16px;
				thead {
						tr {
								background-color: #A83EA2;
								color: white;
								font-weight: bold;
						}
						th {
								padding: 10px;
								border: 1px solid #ddd;
						}
				}
				tbody {
						tr {
								border-bottom: 1px solid #ddd;
								&:hover {
										background-color: #ddd;
								}
						}
						td {
								padding: 8px;
						}
				}
		}
		.peerArticles {
				margin: 40px 0px;
				padding: 16px;
								@apply bg-slate-100;
								@media (prefers-color-scheme: dark) {
										background-color: #4b5563; /* slate-500 */
								}
				border-radius: 5px;
				ion-icon {
						padding-right: 8px;
						font-size: xx-large;
				}
				h4 {
												@apply text-2xl font-bold;
						display: inline-flex;
						align-items: center;
				}
				ul {
						@media screen and (max-width: 499px) {
								padding-left: 0px
						}
				}
				li {
						@media screen and (max-width: 499px) {
								list-style-type: none;
						}
				}
		}
		.faq {
				h3 {
						@apply text-2xl font-bold text-gray-500;
				}
		}
		.topArticles {
				margin: 20px 0px 60px 0px;
				padding: 16px;
				border-radius: 5px;
								@apply bg-slate-100;
								@media (prefers-color-scheme: dark) {
										background-color: #4b5563; /* slate-500 */
								}
				ion-icon {
						padding-right: 8px;
						font-size: xx-large;
				}
				h4 {
						display: inline-flex;
						align-items: center;
												@apply text-2xl font-bold;
				}
				UL {
						@media screen and (max-width: 499px) {
								padding-left: 0px
						}
				}
				LI {
						list-style-type: none;
				}
		}
		.TOCUP {
				cursor: pointer;
				margin-left: 8px;
				text-transform: lowercase;
				font-weight: 200;
				font-size: 2.0rem;
				display: inline;
				@apply border-b border-dashed border-nucamp-light hover:text-nucamp-light-hover;
		}
		.tldr {
				@apply bg-slate-100;
				@media (prefers-color-scheme: dark) {
						background-color: #4b5563; /* slate-500 */
				}

				margin: 20px 0px 0px 0px;
				padding: 16px;
				border-radius: 5px;
				h4 {
						display: inline-flex;
						align-items: center;
												@apply text-2xl font-bold;
				}
				p {
						margin: 14px 0px;
						font-style: italic;
				}
		}
	}
</style>
